// Wspólność małżeńska w recenzji propozycji AI z wypisów.
//
// Małżeństwo wskazuje WPROST wypis: ekstrakcja wpisuje obojgu tę samą `marital_group`
// (nic nie jest wnioskowane z nazwiska, adresu ani z równych udziałów — zob.
// wypisy/extraction.py). Para to JEDNA jednostka zgody z JEDNYM udziałem, więc recenzja
// pokazuje małżonków razem i liczy ich udział raz: dwa wiersze po „484/1000” czytały się
// jak 968/1000, choć działka ma komplet 100%.
//
// Ta sama osoba potrafi mieć na działce udział samodzielny (własna pozycja Lp) ORAZ
// drugi w małżeństwie. W propozycji to DWA wpisy o tym samym nazwisku: małżeński (z
// grupą) i samodzielny (bez grupy). Podpis jest jeden, więc wpis samodzielny wchodzi do
// jednostki małżeństwa jako techniczny duplikat osoby (`solo`) — wiersz z własnym
// udziałem obok wiersza z udziałem wspólnym — a udział jednostki to suma obu. Jedyny
// przypadek, gdy jednostka ma (technicznie) trzy wiersze, a osoby dwie.
//
// Lustro backendowego `apps/dlc_01_power_project/shares.py` (`owner_units`, `sum_shares`,
// `share_text`) i `wypisy/marital.py` (skład małżeństwa) — udziału, którego nie da się
// odczytać, nikt tu nie domyśla.
import { parseShareValue } from "../../ownerDisplay";

export interface ShareOwnerLike {
  full_name?: string;
  share: string;
  marital_group?: string;
  /** Pozycja Lp z wypisu (ekstrakcja) — po niej rozstrzyga się, który z dwóch wpisów
   *  tej samej osoby jest małżeński. */
  lp?: string;
  /** Podmiot pominięty przy zapisie (zdublowany urząd) — nie wchodzi do sumy. */
  redundant?: boolean;
}

/** Nazwisko do porównań: bez wielkości liter i nadmiarowych spacji. */
export const nameKey = (o: { full_name?: string }): string =>
  (o.full_name ?? "").toLocaleLowerCase("pl").split(/\s+/).filter(Boolean).join(" ");

const groupOf = (o: ShareOwnerLike): string => (o.marital_group ?? "").trim();
const lpOf = (o: ShareOwnerLike): string => ((o.lp ?? "").match(/\d+/)?.[0] ?? "").replace(/^0+(?=\d)/, "");

export interface OwnerUnit {
  /** Indeksy wpisów tworzących udział jednostki: para małżeńska albo jeden wpis. */
  marital: number[];
  /** Techniczne duplikaty: udziały samodzielne małżonków (wpisy bez grupy o nazwisku
   *  członka pary). Dolicza się je do udziału jednostki; podpisu nie dokładają. */
  solo: number[];
  married: boolean;
  /** Kolejność wyświetlania: dla każdego małżonka najpierw jego udział samodzielny,
   *  potem wpis małżeński. */
  order: number[];
  /** Skład niezgodny z wypisem (np. trzy różne osoby w jednym małżeństwie) — do
   *  ręcznej poprawki; puste = OK. */
  warning: string;
}

/** Kanoniczna postać wpisów (lustro `marital.normalize_marital_entries`, krok 1):
 *  gdy TA SAMA osoba ma w jednej grupie dwa wpisy (model oznaczył małżeństwem także jej
 *  własną pozycję Lp), małżeński zostaje ten ze wspólną pozycją Lp współmałżonka —
 *  w odwodzie sąsiadujący z nim na liście, ostatecznie ostatni — a reszta traci grupę
 *  (udział samodzielny). Stare, zapisane propozycje nie przeszły sanityzacji backendu. */
export function canonicalizeMarital<T extends ShareOwnerLike>(owners: T[]): T[] {
  const byGroup = new Map<string, number[]>();
  owners.forEach((o, j) => {
    const key = groupOf(o);
    if (key) byGroup.set(key, [...(byGroup.get(key) ?? []), j]);
  });
  const drop = new Set<number>();
  for (const members of byGroup.values()) {
    const byName = new Map<string, number[]>();
    members.forEach((j) => {
      const name = nameKey(owners[j]);
      byName.set(name, [...(byName.get(name) ?? []), j]);
    });
    for (const [name, same] of byName) {
      if (!name || same.length < 2) continue;
      const others = members.filter((j) => nameKey(owners[j]) !== name);
      const otherLps = new Set(others.map((j) => lpOf(owners[j])).filter(Boolean));
      const keep =
        same.find((j) => otherLps.has(lpOf(owners[j]))) ??
        same.find((j) => others.some((o) => Math.abs(o - j) === 1)) ??
        same[same.length - 1];
      same.forEach((j) => j !== keep && drop.add(j));
    }
  }
  return drop.size ? owners.map((o, j) => (drop.has(j) ? { ...o, marital_group: "" } : o)) : owners;
}

/** Właściciele złożeni w jednostki zgody (= podpisy), w kolejności wejściowej. */
export function ownerUnits<T extends ShareOwnerLike>(owners: T[]): OwnerUnit[] {
  const byGroup = new Map<string, number[]>();
  const singles: number[] = [];
  owners.forEach((o, j) => {
    const key = groupOf(o);
    if (key) byGroup.set(key, [...(byGroup.get(key) ?? []), j]);
    else singles.push(j);
  });
  // Pass 1: udziały samodzielne małżonków → przypisane do ich małżeństwa (mogą stać
  // w propozycji PRZED parą, więc nie da się tego rozstrzygnąć w jednym przebiegu).
  const soloOf = new Map<string, number[]>();
  const claimed = new Set<number>();
  for (const [key, members] of byGroup) {
    if (members.length < 2) continue;
    const solo: number[] = [];
    for (const m of members) {
      const name = nameKey(owners[m]);
      if (!name) continue;
      for (const s of singles) {
        if (!claimed.has(s) && nameKey(owners[s]) === name) {
          claimed.add(s);
          solo.push(s);
        }
      }
    }
    soloOf.set(key, solo);
  }
  // Pass 2: jednostki w kolejności pierwszego wystąpienia.
  const units: OwnerUnit[] = [];
  const done = new Set<string>();
  owners.forEach((o, j) => {
    if (claimed.has(j)) return;
    const key = groupOf(o);
    const members = key ? byGroup.get(key)! : [j];
    if (key) {
      if (done.has(key)) return;
      done.add(key);
    }
    if (members.length < 2) {
      units.push({ marital: [j], solo: [], married: false, order: [j], warning: "" });
      return;
    }
    const solo = soloOf.get(key) ?? [];
    const order: number[] = [];
    for (const m of members) {
      const name = nameKey(owners[m]);
      order.push(...solo.filter((s) => nameKey(owners[s]) === name), m);
    }
    const distinct = new Set(members.map((m) => nameKey(owners[m]))).size;
    const warning =
      distinct > 2
        ? `${distinct} różne osoby w jednym małżeństwie — wypis wskazuje małżeństwo dwojga; wyłącz osobę z własnej pozycji Lp (osobny podpis).`
        : "";
    units.push({ marital: members, solo, married: true, order, warning });
  });
  return units;
}

export interface OwnerSlot<T> {
  /** Indeks właściciela w ORYGINALNEJ tablicy — po nim adresuje edycja i wynik
   *  dopasowania urzędów, więc kolejność wyświetlania nie może go zmieniać. */
  j: number;
  owner: T;
  /** Indeksy wpisów małżeńskich jednostki (para) — wspólny udział edytuje się na nich. */
  unit: number[];
  /** Wpis małżeński trzymający pole wspólnego udziału (u singla zawsze true). */
  first: boolean;
  /** Pierwszy wiersz jednostki w kolejności wyświetlania — nagłówek grupy. */
  head: boolean;
  married: boolean;
  /** Techniczny duplikat: udział samodzielny małżonka obok wspólności. */
  solo: boolean;
  /** Drugi wpis tej samej osoby POZA małżeństwem — apply zapisze tylko pierwszy. */
  duplicate: boolean;
  /** Ostrzeżenie o składzie jednostki (tylko na `head`). */
  warning: string;
}

/** Właściciele w kolejności do wyświetlenia: małżonkowie sąsiadują (nawet gdy w
 *  dokumencie rozdzielił ich ktoś trzeci), udział samodzielny małżonka stoi przy jego
 *  wpisie małżeńskim, każdy z zachowanym indeksem wejściowym. */
export function ownerSlots<T extends ShareOwnerLike>(owners: T[]): OwnerSlot<T>[] {
  const slots: OwnerSlot<T>[] = [];
  const singleNames = new Set<string>();
  for (const u of ownerUnits(owners)) {
    let headDone = false;
    let firstDone = false;
    for (const j of u.order) {
      const solo = u.solo.includes(j);
      const first = !u.married || (!solo && !firstDone);
      if (!solo) firstDone = true;
      const name = nameKey(owners[j]);
      const duplicate = !u.married && !!name && singleNames.has(name);
      if (!u.married && name) singleNames.add(name);
      slots.push({
        j,
        owner: owners[j],
        unit: u.marital,
        first,
        head: !headDone,
        married: u.married,
        solo,
        duplicate,
        warning: headDone ? "" : u.warning,
      });
      headDone = true;
    }
  }
  return slots;
}

/** Udział jednostki zgody: dla pary PIERWSZY niepusty zapis — w wypisie bywa wpisany
 *  tylko przy jednym z małżonków. */
export function unitShare<T extends ShareOwnerLike>(owners: T[], unit: number[]): string {
  return unit.map((m) => owners[m]?.share ?? "").find((s) => s.trim()) ?? "";
}

export interface ShareSum {
  /** Suma TYLKO odczytanych udziałów (1 = 100%). */
  total: number;
  /** Jednostki zgody = podpisy do zebrania. */
  units: number;
  /** Jednostki bez czytelnego zapisu udziału — bez tego „100%” bywałoby zgadywane. */
  unknown: number;
}

/** Suma udziałów działki po jednostkach zgody: para liczona raz (wspólny udział +
 *  udziały samodzielne małżonków), pominięci wyłączeni. */
export function unitShareSum<T extends ShareOwnerLike>(owners: T[]): ShareSum {
  let total = 0;
  let units = 0;
  let unknown = 0;
  for (const u of ownerUnits(owners)) {
    const marital = u.marital.filter((m) => !owners[m].redundant);
    const solo = u.solo.filter((m) => !owners[m].redundant);
    if (!marital.length && !solo.length) continue;
    units += 1;
    const parts = [...(marital.length ? [unitShare(owners, marital)] : []), ...solo.map((m) => owners[m].share)];
    const values = parts.map(parseShareValue);
    if (values.some((v) => v === null)) unknown += 1;
    else total += values.reduce<number>((a, b) => a + (b ?? 0), 0);
  }
  return { total, units, unknown };
}

/** „100%”, „66,7%” — jak `shares.share_text` na backendzie. */
export function percentText(value: number): string {
  const rounded = Math.round(value * 1000) / 10;
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1).replace(".", ",")}%`;
}
